import React from "react";

import Selector, { type SelectorOption, type SingleValue } from "~/ui/selectors/Selector";

import type { UserMeetingRecapSendTo } from "~/__generated__/MeetingCopilotAutoShareSettingsMutation.graphql";

import styles from "./css/settings.module.css";

export type AttendeeValueOptions = "all" | "internal-attendees" | "just-me" | "none";

function formatEmailDomains(domains: readonly string[]): string {
	if (domains.length === 0) return "";
	if (domains.length === 1) return `@${domains[0]}`;
	if (domains.length === 2) return `@${domains[0]} and @${domains[1]}`;
	return `${domains
		.slice(0, -1)
		.map(domain => `@${domain}`)
		.join(", ")}, and @${domains[domains.length - 1]}`;
}

export default function RecapSettingsSelector({
	value,
	onChange,
	emailDomainList,
	width,
	lockToValue,
}: {
	value: AttendeeValueOptions;
	onChange: (value: AttendeeValueOptions) => void;
	emailDomainList: readonly string[];
	width?: string;
	lockToValue?: boolean;
}) {
	const handleChange = React.useCallback(
		(selectedOption: SingleValue<SelectorOption>) => {
			if (!selectedOption) return;
			onChange(selectedOption.value as AttendeeValueOptions);
		},
		[onChange],
	);

	const autoShareLabels = {
		all: (
			<>
				All meeting invitees
				{!lockToValue && <span className={styles.recommendedLabel}>Recommended</span>}
			</>
		),
		"internal-attendees":
			emailDomainList.length === 1 ? <>Only @{emailDomainList[0]} invitees</> : <>Only invitees from your org</>,
		"just-me": <>Only Fellow users</>,
		none: <>No one</>,
	};

	return (
		<div>
			<Selector
				width={width}
				value={{
					value,
					label: autoShareLabels[value] as unknown as string,
				}}
				options={Object.entries(autoShareLabels).map(([val, label]) => ({
					value: val,
					label: label as unknown as string,
					isDisabled: lockToValue && val !== value,
					hoverTooltipWhenDisabled: "This setting has been locked by your workspace admin and cannot be changed.",
				}))}
				onChange={handleChange}
			/>
			{value === "internal-attendees" && emailDomainList.length > 1 && (
				<div className={styles.emailDomains}>{formatEmailDomains(emailDomainList)}</div>
			)}
		</div>
	);
}

export const mapUIAttendeeValueToUserMeetingRecapSendTo: Record<AttendeeValueOptions, UserMeetingRecapSendTo> = {
	all: "ALL",
	"internal-attendees": "INTERNAL",
	"just-me": "JUST_ME",
	none: "NONE",
};

export const mapUserMeetingRecapSendToToUIAttendeeValue: Record<UserMeetingRecapSendTo, AttendeeValueOptions> = {
	ALL: "all",
	INTERNAL: "internal-attendees",
	JUST_ME: "just-me",
	NONE: "none",
};
